import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { listFactors, unenrollFactor, getMyMfaStatus } from "@/lib/mfa";
import { MFA_ENABLED, mustEnforceMfa } from "@/config/mfaFlags";
import { useAuth } from "@/context/Auth";
import MfaEnrollDialog from "@/components/auth/MfaEnrollDialog";
import SEO from "@/components/seo/SEO";

export default function AccountSecurity() {
  const { session, roles, tier } = useAuth();
  const navigate = useNavigate();
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [lastVerifiedAt, setLastVerifiedAt] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const [{ verified }, status] = await Promise.all([listFactors(), getMyMfaStatus()]);
    setEnrolled(verified.length > 0);
    setFactorId(verified[0]?.id ?? null);
    setLastVerifiedAt(status?.last_verified_at ?? null);
  };

  useEffect(() => {
    refresh();
  }, []);

  const enforced = mustEnforceMfa({
    email: session?.user?.email,
    roles,
    tier,
  });

  const onDisable = async () => {
    if (!factorId || enforced) return;
    setBusy(true);
    try {
      await unenrollFactor(factorId);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <>
      <SEO title="Account Security | CSV Pro" description="Manage your two-factor authentication." />
      <div className="min-h-screen bg-background">
        {/* Top bar with safe back navigation */}
        <div className="border-b bg-background/80 backdrop-blur sticky top-0 z-10">
          <div className="max-w-3xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
            <Button variant="ghost" size="sm" onClick={goBack} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/")}>
              Dashboard
            </Button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Account Security</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              Manage how you sign in to CSV Pro.
            </p>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  {enrolled ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
                </div>
                <div>
                  <CardTitle className="text-lg sm:text-xl">Two-Factor Authentication (2FA)</CardTitle>
                  <CardDescription className="mt-1">
                    Add a second verification step at sign-in using an authenticator app
                    (Google Authenticator, 1Password, Microsoft Authenticator, Authy, etc.).
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {!MFA_ENABLED && (
                <p className="text-sm text-muted-foreground">
                  Two-factor authentication is currently disabled platform-wide.
                </p>
              )}

              {MFA_ENABLED && enrolled === null && (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}

              {MFA_ENABLED && enrolled === true && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-800/60 dark:bg-emerald-950/40">
                    <ShieldCheck className="h-5 w-5 text-emerald-700 dark:text-emerald-300 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-emerald-900 dark:text-emerald-100">
                        2FA Enabled
                      </p>
                      <p className="text-sm text-emerald-800/80 dark:text-emerald-200/80 mt-0.5">
                        Your account is protected with an authenticator app.
                      </p>
                      {lastVerifiedAt && (
                        <p className="text-xs text-emerald-800/70 dark:text-emerald-200/70 mt-2">
                          Last verified: {new Date(lastVerifiedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  {enforced ? (
                    <p className="text-sm text-muted-foreground">
                      Your role requires two-factor authentication. To reset it, please contact an administrator.
                    </p>
                  ) : (
                    <Button variant="outline" onClick={onDisable} disabled={busy}>
                      {busy ? "Disabling…" : "Disable two-factor authentication"}
                    </Button>
                  )}
                </div>
              )}

              {MFA_ENABLED && enrolled === false && (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800/60 dark:bg-amber-950/40">
                    <ShieldAlert className="h-5 w-5 text-amber-700 dark:text-amber-300 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-900 dark:text-amber-100">
                        2FA is not yet enabled
                      </p>
                      <p className="text-sm text-amber-800/80 dark:text-amber-200/80 mt-0.5">
                        Protect your account against credential theft with a second verification step.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => setDialogOpen(true)} className="sm:w-auto w-full">
                      Enable 2FA
                    </Button>
                    <Button variant="ghost" onClick={goBack} className="sm:w-auto w-full">
                      Not now
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <MfaEnrollDialog open={dialogOpen} onOpenChange={setDialogOpen} onCompleted={refresh} />
      </div>
    </>
  );
}
